import { useForm, ValidationError } from '@formspree/react';
import gardenArt from '../images/gardenSignR.jpg';
import React, { useState, useRef, useEffect } from 'react';
import BlurImage from './BlurImage';

const GARDEN_MAP_COORDINATES = '39.97276832377213,-75.13826702552127';
const GARDEN_MAP_URL =
  `https://www.google.com/maps?q=${GARDEN_MAP_COORDINATES}&z=20&t=k`;
const GARDEN_MAP_EMBED_URL =
  `https://maps.google.com/maps?q=${GARDEN_MAP_COORDINATES}&ll=${GARDEN_MAP_COORDINATES}&z=20&t=k&output=embed`;

function ContactForm() {
  const [state, handleSubmit] = useForm('xqakyelp');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const formRef = useRef(null);

  const handleWrappedSubmit = async (event) => {
    setHasSubmitted(true);
    await handleSubmit(event);
  };

  const showGlobalError =
    hasSubmitted && !state.submitting && !state.succeeded && !Array.isArray(state.errors);

  useEffect(() => {
    if (showGlobalError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showGlobalError]);

  if (state.succeeded) {
    return (
      <div className="min-h-screen bg-[#f8f4ea] flex items-center justify-center px-6 py-24">
        <div className="text-center">
          <h1 className="font-serif text-5xl text-[#10351f]">Thanks for reaching out!</h1>
          <div className="mt-5 h-px w-16 bg-[#304536] mx-auto" />
          <p className="mt-6 text-lg text-[#445044]">
            We'll be in touch soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">

      {/* ── Page hero ─────────────────────────────────────────────── */}
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-20 lg:px-16">
          <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl">
            Get In Touch
          </h1>
          <div className="mt-5 h-px w-24 bg-[#304536]" />
          <p className="font-handwritten mt-5 max-w-xl text-xl leading-snug text-[#243c2c] sm:text-2xl">
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* ── Global error banner ───────────────────────────────────── */}
      {showGlobalError && (
        <div className="border-b border-[#d8d0be] bg-[#fdf0ed] px-6 py-5 text-center">
          <p className="font-semibold text-[#a43b29]">Sorry, our site is having some problems.</p>
          <p className="mt-1 text-sm text-[#445044]">
            You can reach us directly at{' '}
            <a
              href="mailto:EmmasCourtCommunityGarden@gmail.com"
              className="underline hover:text-[#8e3a2b]"
            >
              EmmasCourtCommunityGarden@gmail.com
            </a>
          </p>
        </div>
      )}

      {/* ── Content grid ──────────────────────────────────────────── */}
      <section className="bg-[#f8f4ea]">
        <div className="mx-auto grid max-w-[92rem] gap-12 px-6 py-14 sm:px-10 md:py-20 lg:grid-cols-2 lg:gap-16 lg:px-16">

          {/* Left — form */}
          {!showGlobalError && (
            <div>
              <div className="mb-6 border-b border-[#cfc7b6] pb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                  Send a message
                </p>
                <h2 className="mt-2 font-serif text-4xl text-[#10351f]">Contact Us</h2>
              </div>

              {/* Contact info */}
              <div className="mb-8 space-y-2 text-sm text-[#4f5c50]">
                <p>
                  <a
                    href={GARDEN_MAP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-[#8e3a2b] underline underline-offset-2"
                  >
                    1404 N Hancock St. Philadelphia, PA 19122
                  </a>
                </p>
                <p>
                  <a
                    href="mailto:EmmasCourtCommunityGarden@gmail.com"
                    className="hover:text-[#8e3a2b] underline underline-offset-2"
                  >
                    EmmasCourtCommunityGarden@gmail.com
                  </a>
                </p>
              </div>

              {/* Form */}
              <form
                className="space-y-4 border border-[#d5cfc0] bg-[#fffdf7] p-6 sm:p-8"
                onSubmit={handleWrappedSubmit}
                ref={formRef}
              >
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-[#70806f]">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    className="w-full border border-[#d5cfc0] bg-[#f8f4ea] px-4 py-3 text-sm text-[#18271e] placeholder-[#adb8a9] outline-none focus:border-[#43513f] focus:ring-1 focus:ring-[#43513f]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-[#70806f]">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    className="w-full border border-[#d5cfc0] bg-[#f8f4ea] px-4 py-3 text-sm text-[#18271e] placeholder-[#adb8a9] outline-none focus:border-[#43513f] focus:ring-1 focus:ring-[#43513f]"
                  />
                  <ValidationError prefix="email" field="email" errors={state.errors} className="mt-1 text-xs text-[#a43b29]" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-[#70806f]">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="_subject"
                    placeholder="What's this about?"
                    className="w-full border border-[#d5cfc0] bg-[#f8f4ea] px-4 py-3 text-sm text-[#18271e] placeholder-[#adb8a9] outline-none focus:border-[#43513f] focus:ring-1 focus:ring-[#43513f]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-[#70806f]">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows="5"
                    placeholder="Your message…"
                    className="w-full border border-[#d5cfc0] bg-[#f8f4ea] px-4 py-3 text-sm text-[#18271e] placeholder-[#adb8a9] outline-none focus:border-[#43513f] focus:ring-1 focus:ring-[#43513f]"
                  />
                  <ValidationError prefix="Message" field="message" errors={state.errors} className="mt-1 text-xs text-[#a43b29]" />
                </div>
                <button
                  type="submit"
                  disabled={state.submitting}
                  className="inline-flex w-full items-center justify-between border border-[#43513f] bg-[#10351f] px-5 py-4 text-sm font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fffdf7]"
                >
                  <span>{state.submitting ? 'Sending…' : 'Send Message'}</span>
                  <span aria-hidden="true">&rarr;</span>
                </button>
              </form>
            </div>
          )}

          {/* Right — image + map */}
          <div className="space-y-8">
            <div className="overflow-hidden border border-[#d3cbbb]">
              <BlurImage
                src={gardenArt}
                alt="Emma's Court garden sign"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Find Us
              </p>
              <h3 className="mb-4 font-serif text-2xl text-[#10351f]">On the Map</h3>
              <div className="overflow-hidden border border-[#d3cbbb]">
                <iframe
                  title="Emma's Court Garden Map"
                  src={GARDEN_MAP_EMBED_URL}
                  width="100%"
                  height="320"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

export default ContactForm;
